import { useAtomValue } from 'jotai'
import type { LucideIcon } from 'lucide-react'
import { z } from 'zod'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'
import { GlobalStore } from '@highlight/ipc'
import { atomWithGlobalStore } from '@highlight/ipc/jotai'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { TOUR_MISSIONS } from './missions'

/** Values a mission's path reads, like the app to connect; the server sets some, the client adds live ones. */
export type MissionParams = Readonly<Record<string, string>>

export interface TourStop {
  /** A `TourMission` id. */
  mission: string
  title: string
  reason: string
  params?: MissionParams
}

/** The window a step's target lives in; each window draws only its own steps. */
export type TourSurface = 'overlay' | 'settings'

export interface MissionStep {
  surface: TourSurface
  /** Matched against `[data-tour-section]`. */
  target: string
  hint: string
  /** Skipped when its target never shows, like Upcoming with no meetings, so missing data can't stall a mission. */
  optional?: boolean
  /** Circled instead while the target is hidden behind something closable, like a menu. */
  recoverVia?: { target: string; hint: string }
  /** Opens something later steps live in; jumps ahead once one of their targets shows, however the user got there. */
  skipWhenLaterShows?: boolean
  /** Never jumped to by skip-ahead, because it showing doesn't mean the earlier steps happened, like Send. */
  neverSkippedTo?: boolean
  /** Nothing to click: done as soon as the target shows, like a page the user only needs to see. */
  completeOnArrive?: boolean
}

/** One place the tour teaches. A new id is offered to everyone who has not seen it, in a round of its own. */
export interface TourMission {
  id: string
  icon: LucideIcon
  /** The real clicks from Home, ending on the thing the stop is about. */
  path: (params: MissionParams) => Array<MissionStep>
}

interface DayZeroTourStore {
  /** Missions from finished rounds; never offered again. */
  seen: Array<string>
  /** The current round: the missions not yet seen when it was generated. */
  stops: Array<TourStop>
  visited: Array<string>
  /** Rounds the user clicked Done on; any round after the first offers only missions added since. */
  roundsFinished?: number
  /** Bumped by a reset; part of the fetch key so a reset generates a fresh tour instead of reusing the cached one. */
  generation?: number
  /** Set by the developer reset: shows the tour whatever the account's age or experiment arm. */
  forced?: boolean
  /** Set while another window generates the round, so the overlay waits on it instead of asking twice. */
  generatingSince?: number | null
}

const TOUR_STORE_KEY = 'sidepanel.forYou.dayZeroTour.v3'
const MISSION_STORE_KEY = 'sidepanel.forYou.activeMission.v2'

const EMPTY_TOUR: DayZeroTourStore = { seen: [], stops: [], visited: [] }
/** A warm-up running longer than this is presumed dead, so the overlay asks for the round itself. */
const WARMING_TIMEOUT_MS = 3 * 60_000

const ResponseSchema = z.object({
  data: z.object({
    status: z.enum(['personalized', 'generic', 'not_ready']),
    stops: z.array(
      z.object({
        mission: z.string(),
        title: z.string(),
        reason: z.string(),
        params: z.record(z.string(), z.string()).optional(),
      }),
    ),
    deferred: z.array(z.string()).default([]),
  }),
})

export type TourRound = z.infer<typeof ResponseSchema>['data'] & { requested: Array<string> }

/** Persisted, so a half-finished round and the seen list survive restarts. */
export const dayZeroTourAtom = atomWithGlobalStore<DayZeroTourStore>('general', TOUR_STORE_KEY, EMPTY_TOUR)

export interface ActiveMission {
  mission: string | null
  title: string
  params: MissionParams
  /** Index into the path; equal to its length once the mission is complete. */
  step: number
  /** Ended by Skip, so no "Mission complete" card shows. */
  skipped?: boolean
}

export const NO_MISSION: ActiveMission = { mission: null, title: '', params: {}, step: 0 }

/** Global store rather than a plain atom: a settings mission starts in the overlay and finishes in another window. */
export const activeMissionAtom = atomWithGlobalStore<ActiveMission>('general', MISSION_STORE_KEY, NO_MISSION)

/** One event for the whole tour, so its funnel is a breakdown on `action`. */
export const trackTour = (action: string, properties?: Record<string, string | number>) =>
  AmplitudeRenderer.trackEvent('Day Zero Tour', { action, ...properties })

export const findMission = (id: string | null): TourMission | undefined => TOUR_MISSIONS.find((m) => m.id === id)

/** The `[data-tour-section]` the active mission circles next, so a virtualized list can bring that row into range. */
export function useActiveTourTarget(): string | null {
  const { mission, params, step } = useAtomValue(activeMissionAtom)
  return findMission(mission)?.path(params)[step]?.target ?? null
}

export const unseenMissions = (seen: ReadonlyArray<string>): Array<string> =>
  TOUR_MISSIONS.map((mission) => mission.id).filter((id) => !seen.includes(id))

/** Milliseconds until a warm-up in flight is presumed dead; 0 when none is running. */
export function warmingRemainingMs(generatingSince: number | null | undefined, now = Date.now()): number {
  return generatingSince ? Math.max(0, generatingSince + WARMING_TIMEOUT_MS - now) : 0
}

export async function requestTourRound(
  getAuthToken: () => Promise<string>,
  missions: Array<string>,
  forced: boolean,
): Promise<TourRound> {
  const force = forced ? '&force=1' : ''
  const url = `${getChatApiRoute('api/v1/onboarding/tour')}?missions=${encodeURIComponent(missions.join(','))}${force}`
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${await getAuthToken()}` },
    signal: AbortSignal.timeout(120_000),
  })
  if (!response.ok) {
    throw new Error(`Day 0 tour request failed: ${response.status}`)
  }
  return { requested: missions, ...ResponseSchema.parse(await response.json()).data }
}

export function applyTourRound(prev: DayZeroTourStore, round: TourRound): DayZeroTourStore {
  if (round.status === 'not_ready' || prev.stops.length > 0) {
    return prev
  }
  const stops = round.stops.filter((stop) => round.requested.includes(stop.mission))
  // A mission the server had no stop for counts as seen, or every launch would ask for it again; a deferred one doesn't.
  const skipped = round.requested.filter(
    (id) => !round.deferred.includes(id) && !stops.some((stop) => stop.mission === id),
  )
  return { ...prev, seen: [...prev.seen, ...skipped], stops, visited: [] }
}

/** Generates the next round from a window other than the overlay, so it is ready by the time For You opens. */
export async function warmDayZeroTour(getAuthToken: () => Promise<string>): Promise<void> {
  const store = GlobalStore.general()
  await store.whenReady()
  const current = store.get<DayZeroTourStore>(TOUR_STORE_KEY) ?? EMPTY_TOUR
  const missions = unseenMissions(current.seen)
  if (current.stops.length > 0 || missions.length === 0 || warmingRemainingMs(current.generatingSince) > 0) {
    return
  }
  store.set<DayZeroTourStore>(TOUR_STORE_KEY, { ...current, generatingSince: Date.now() })
  const round = await requestTourRound(getAuthToken, missions, Boolean(current.forced)).catch((error: unknown) => {
    console.error('[DayZeroTour] warm-up failed', error)
    return null
  })
  const latest = store.get<DayZeroTourStore>(TOUR_STORE_KEY) ?? EMPTY_TOUR
  // A reset since the request started owns the store now.
  if (latest.generation !== current.generation) {
    return
  }
  store.set<DayZeroTourStore>(TOUR_STORE_KEY, {
    ...(round ? applyTourRound(latest, round) : latest),
    generatingSince: null,
  })
}

/** Developer reset: forgets every round and any mission in flight, and makes the overlay generate a new tour for any account. */
export function resetDayZeroTour(): void {
  const previous = GlobalStore.general().get<DayZeroTourStore>(TOUR_STORE_KEY)
  GlobalStore.general().set<DayZeroTourStore>(TOUR_STORE_KEY, {
    ...EMPTY_TOUR,
    generation: (previous?.generation ?? 0) + 1,
    forced: true,
  })
  GlobalStore.general().set<ActiveMission>(MISSION_STORE_KEY, NO_MISSION)
}
